/**
 * Generic dispatcher for client-side completion validation.
 *
 * The rules live on each extension handler's `validateCompletionClient`
 * method \u2014 this file only looks up the handler via `extensionRegistry` and
 * delegates. The "publish" side is Zustand store mutations (they notify
 * subscribers on every edit); the "subscribe" side is the hooks and effects
 * that call this dispatcher and re-render.
 */

import { extensionRegistry } from "@shared/core/index"
import type {
  ClientImageLabelAnnotation,
  ClientLabelRasterAnnotation,
  ClientMaskAnnotation,
  ClientScoreAnnotation,
  ClientTextAnnotation,
  ClientVectorAnnotation
} from "@shared/extensions/_interface"
import type {
  ImageLabelAnnotationState,
  LabelRasterAnnotationState,
  MaskAnnotationState,
  ScoreAnnotationState,
  TextAnnotationState,
  VectorAnnotationState
} from "@shared/lib/history-types"

export interface CompletionCheckResult {
  valid: boolean
  errors: string[]
}

const OK: CompletionCheckResult = { valid: true, errors: [] }

/**
 * Minimal project-module shape accepted by the dispatcher.
 *
 * Both `pages/case-workspace/radiology/types.ProjectModuleItem` and
 * `pages/case-workspace/pathology/types.ProjectModuleItem` are structural
 * supertypes of this, so either can be passed without adapters.
 */
export interface DispatcherProjectModule {
  id: string
  template_type: string
  template_name: string
  config: string | null
  dependencies: ReadonlyArray<{
    depends_on_module_id: string
    dependency_mode: string
    source_label_names?: readonly string[]
  }>
}

export interface ValidateModuleCompletionInput {
  module: DispatcherProjectModule | null
  modules: readonly DispatcherProjectModule[]
  masks?: readonly MaskAnnotationState[]
  scores?: readonly ScoreAnnotationState[]
  vectors?: readonly VectorAnnotationState[]
  texts?: readonly TextAnnotationState[]
  imageLabels?: readonly ImageLabelAnnotationState[]
  labelRasters?: readonly LabelRasterAnnotationState[]
}

function toClientLabelRasters(rasters: readonly LabelRasterAnnotationState[]): ClientLabelRasterAnnotation[] {
  return rasters.map((r) => ({
    id: r.id,
    projectModuleId: r.projectModuleId,
    seriesId: r.seriesId,
    labelName: r.labelName,
    chunkCount: r.chunks.length,
    changedPixels: r.chunks.reduce((n, c) => n + c.changedPixels, 0)
  }))
}

/**
 * Validate a module's completion readiness by dispatching to the extension
 * handler registered for its `template_type`. Handlers that don't implement
 * `validateCompletionClient` are treated as always-valid.
 */
export function validateModuleCompletion(input: ValidateModuleCompletionInput): CompletionCheckResult {
  const { module } = input
  if (!module) return OK

  const handler = extensionRegistry.getHandler(module.template_type)
  if (!handler?.validateCompletionClient) return OK

  return handler.validateCompletionClient({
    module,
    modules: input.modules,
    masks: (input.masks ?? []) as readonly ClientMaskAnnotation[],
    scores: (input.scores ?? []) as readonly ClientScoreAnnotation[],
    vectors: (input.vectors ?? []) as readonly ClientVectorAnnotation[],
    texts: (input.texts ?? []) as readonly ClientTextAnnotation[],
    imageLabels: (input.imageLabels ?? []) as readonly ClientImageLabelAnnotation[],
    labelRasters: toClientLabelRasters(input.labelRasters ?? [])
  })
}
